import React, { memo, useEffect, useState } from "react";
import { Control, UseFormSetValue } from "react-hook-form";

import HierarchicalMultiSelect, {
  Option,
} from "@/components/HierarchicalMultiSelect";
import SelectField from "@/components/SelectField";
import {
  optionsAccountType,
  optionsBucket,
  optionsFirstEmi,
  optionsMob,
  optionsOverdueAmountBand,
  optionsPaymentFrequency,
  optionsSecondEmi,
  optionsThirdEmi,
  optionsValueAtRisk,
} from "@/partial/campaign/constant";
import { CampaignForm } from "@/partial/campaign/types";
import { useDealerMaster } from "@/services/hooks/loan-params/useLoanParams";
import usePortfolioCategory from "@/services/hooks/portfolio/usePortfolio";
import {
  DealerMasterParams,
  PortfolioItem,
  PortfolioItemChild,
} from "@/services/network/type";
import useCampaignStore from "@/store/campaignStore";
import { Flex, Title } from "../../styles";

interface LoanParametersProps {
  isEdit: boolean;
  control: Control<CampaignForm>;
  setValue: UseFormSetValue<CampaignForm>;
}

export const LoanParameters = memo(
  ({ isEdit, control, setValue }: LoanParametersProps) => {
    const { loanParametersOptions: loanParametersOptionsStore } =
      useCampaignStore();

    const [optionsDealerBranch, setOptionsDealerBranch] = useState<
      {
        label: string;
        value: string;
      }[]
    >([]);
    const [portfolioOptions, setPortfolioOptions] = useState<Option[]>([]);

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [searchParams, setSearchParams] = useState<DealerMasterParams>({
      limit: 10,
    });
    const { data, refetch } = useDealerMaster(searchParams);
    const { data: portfolioCategory } = usePortfolioCategory();

    useEffect(() => {
      if (data) {
        setOptionsDealerBranch(
          data.map((dealer) => ({
            label: dealer.dealerName,
            value: dealer.dealerName,
          }))
        );
      }
    }, [data]);

    useEffect(() => {
      if (portfolioCategory) {
        setPortfolioOptions(
          portfolioCategory.map((item: PortfolioItem) => ({
            label: item.name,
            value: item.name,
            name: item.name,
            children: item.items.map((child: PortfolioItemChild) => ({
              label: child.value || child.name || "",
              value: child.id,
              name: child.name,
            })),
          }))
        );
      }
    }, [portfolioCategory]);

    useEffect(() => {
      refetch();
    }, [searchParams, refetch]);

    useEffect(() => {
      if (isEdit && loanParametersOptionsStore) {
        setValue(
          "portfolio",
          loanParametersOptionsStore.optionsPortfolioCategory
        );
        setValue(
          "dealer_branch",
          loanParametersOptionsStore.optionsDealerBranch
        );
      }
    }, [isEdit, loanParametersOptionsStore, setValue]);

    return (
      <React.Fragment>
        <Title>Loan Parameters</Title>
        <Flex>
          <SelectField
            name="account_type"
            label="Account Type"
            placeholder="Select Account Type"
            // isRequired
            isMultiple
            control={control}
            options={optionsAccountType}
            disabled={isEdit}
          />
          <SelectField
            name="mob"
            label="MOB"
            placeholder="Select MOB"
            // isRequired
            control={control}
            options={optionsMob}
            disabled={isEdit}
          />
          <SelectField
            name="bucket"
            label="Bucket"
            placeholder="Select Bucket"
            // isRequired
            isMultiple
            withCheckbox
            control={control}
            options={optionsBucket}
            disabled={isEdit}
          />
          <HierarchicalMultiSelect
            options={portfolioOptions}
            control={control}
            // isRequired
            name="portfolio"
            label="Portfolio"
            rootTitle="All Portfolios"
            placeholder="Select Portfolio"
            disabled={isEdit}
          />
          <SelectField
            name="overdue_amount_band"
            label="Overdue Amount Band"
            placeholder="Select Overdue Amount Band"
            // isRequired
            isMultiple
            withCheckbox
            control={control}
            options={optionsOverdueAmountBand}
            disabled={isEdit}
          />
          <SelectField
            name="value_at_risks"
            label="Value At Risks"
            placeholder="Select Value At Risks"
            // isRequired
            isMultiple
            withCheckbox
            control={control}
            options={optionsValueAtRisk}
            disabled={isEdit}
          />
          <SelectField
            name="first_emi"
            label="First EMI"
            placeholder="Select First EMI"
            // isRequired
            control={control}
            options={optionsFirstEmi}
            disabled={isEdit}
          />
          <SelectField
            name="second_emi"
            label="Second EMI"
            placeholder="Select Second EMI"
            // isRequired
            control={control}
            options={optionsSecondEmi}
            disabled={isEdit}
          />
          <SelectField
            name="third_emi"
            label="Third EMI"
            placeholder="Select Third EMI"
            // isRequired
            control={control}
            options={optionsThirdEmi}
            disabled={isEdit}
          />
          <SelectField
            name="dealer_branch"
            label="Dealer Branch"
            placeholder="Select Dealer Branch"
            // isRequired
            isMultiple
            withCheckbox
            control={control}
            options={isEdit ? [] : optionsDealerBranch}
            disabled={isEdit}
          />
          {/* TODO: remove this when the API is ready */}
          {/* <SelectField
          name="dealer_pincode"
          label="Dealer Pincode"
          placeholder="Select Dealer Pincode"
          // isRequired
          isMultiple
          withCheckbox
          control={control}
          options={optionsDealerPincode}
        /> */}
          <SelectField
            name="payment_frequency"
            label="Payment Frequency"
            placeholder="Select Payment Frequency"
            // isRequired
            control={control}
            options={optionsPaymentFrequency}
            disabled={isEdit}
          />
        </Flex>
      </React.Fragment>
    );
  }
);
export default LoanParameters;
